<script setup>
import StartupsHeader from "@/components/ecosystem/StartupsHeader.vue";
import useGrid from "@/composables/useGrid";
import {getLogo} from "@/helpers/logo";
import {onMounted, ref, watch} from "vue";
import TablePager from "@/components/TablePager.vue";
import startupsService from "@/services/ecosystem/startups.service";
import {inviteStatuses} from "@/constants/constants";
import useStoreTrigger from "@/composables/useStoreTrigger";
import {useStore} from "vue2-helpers/vuex";
import AddToPortfolios from "@/components/startups/AddToPortfolios.vue";
import Invite from "@/components/startups/InviteStartup.vue";

const columns = [
    {
        label: 'Name',
        name: 'name',
        component: 'SearchCompanyNameColumn',
    },
    {
        label: 'Stage',
        name: 'stage',
    },
    {
        label: 'Location',
        name: 'location',
        component: 'SearchCompanyLocationColumn',
    },
    {
        label: 'Total Raised',
        name: 'total_raised',
    },
    {
        label: 'Status',
        name: 'status',
        component: 'InviteStatusColumn',
    },
    {
        name: 'actions',
        label: 'Actions',
        component: 'Actions',
    }
];

const {items, listLoading, gridParams, gridMethods} = useGrid(getIndexList);
const store = useStore();
const storeTrigger = useStoreTrigger();

const removing = ref(false);
const inviteFilter = ref('all');
const selectedStartups = ref([]);
const addStartupsToPortfolios = ref(null);
const invite = ref(null);

watch(inviteFilter, value => {
    gridParams.filters.invite_status = value
    gridMethods.resetList();
})

watch(() => store.state.needUpdateStartups, () => {
    if (store.state.needUpdateStartups) {
        gridMethods.resetList();
    }
})

onMounted(() => {
    gridMethods.setColumns(columns);
    gridMethods.resetList();
})

function getIndexList(pageParams) {
    return startupsService.getMyStartupsIndex(pageParams);
}

function applySearch(searchString) {
    gridParams.filters.query = searchString.trim();
    gridMethods.resetList();
}

async function remove(startupId) {
    if (confirm("Remove startup?")) {
        removing.value = true;
        await startupsService.deleteStartup(startupId);
        removing.value = false;

        gridMethods.resetList();
    }
}

function addToPortfolio(startupId) {
    selectedStartups.value = [startupId];
    addStartupsToPortfolios.value.open(startupId);
}
</script>

<template>
    <div class="mystartups-page dashboard-page">
        <header class="topbar mystartups-page__header">
            <StartupsHeader/>

            <input
                type="text"
                class="form-search-primary"
                placeholder="Search Startups"
                v-debounce:500ms.lock="applySearch"
                :disabled="listLoading"
            >
            <div class="topbar__side actions-list">
                <b-dropdown
                    id="dropdown-show"
                    :text="inviteStatuses[inviteFilter]"
                    class="topbar__side__actions topbar__side__actions--filter"
                >
                    <b-dropdown-item
                        v-for="(label, name) in inviteStatuses"
                        :key="name"
                        @click="inviteFilter = name"
                    >
                        {{label}}
                    </b-dropdown-item>
                </b-dropdown>

                <a class="link-wrapper ml-4">
                    <span class="link-wrapper__add" @click="storeTrigger('setAddStartup');">Add Startups</span>
                </a>
            </div>
        </header>

        <aside class="mystartups-page__aside">
            <div class="dashboard-page__card">
                <div class="dashboard-page__card__inner pb-4">
                    <h3 class="dashboard-page__title">Your network</h3>

                    <p>Add your portfolio startups in order to recommend them to enteprise companies</p>

                    <button class="button button-primary button--lg w-100">Add Startups</button>
                </div>
            </div>
            <div class="dashboard-page__card">
                <div class="dashboard-page__card__inner pb-4">
                    <h3 class="dashboard-page__title">Invite startups</h3>

                    <p>13 out of your 1,083 startups have claimed their profiles on SwitchPitch</p>

                    <button class="button button-bordered button--lg w-100">Invite Startup</button>
                </div>
            </div>

<!-- When empty -->
            <div class="dashboard-page__card">
                <div class="dashboard-page__card__inner pb-4">
                    <h3 class="dashboard-page__title">Add Startups</h3>

                    <p>Add your portfolio startups in order to recommend them to enteprise companies</p>

                    <button class="button button-primary button--lg w-100">Add Startups</button>
                </div>
            </div>
        </aside>
        <main class="mystartups-page__main">
            <ul class="stat-overview">
                <li>Total Startups <big>1,083</big></li>
                <li>Submitted to Needs <big>44</big> </li>
                <li>Under Review <big>29</big></li>
                <li>Accepted <big>12</big></li>
                <li>Escalated <big>6</big></li>
            </ul>

            <div class="dashboard-page__cards2">
                <div class="dashboard-page__card-basic">
                    <h6 class="heading6">Submissions recommended by switchpitch</h6>

                    <img src="/img-graph1.png" alt=""/>
                </div>
                <div class="dashboard-page__card-basic">
                    <h6 class="heading6">Top suggested Categories</h6>

                    <img src="/img-graph2.png" alt=""/>
                </div>
            </div>

            <div id="" class="dashboard-page__card dashboard-page__card--round">
                <div class="dashboard-page__card__inner">
                    <header class="dashboard-page__card__header">
                        <h3 class="dashboard-page__title">My Startups</h3>

                        <div class="ml-auto">
                            <input type="text" placeholder="Search for a startup or category" class="dashboard-page__search dashboard-page__search--md">
                        </div>
                    </header>
                </div>

                <div class="dashboard-page__table dashboard-page__table-flexible">
                    <table>
                        <thead>
                            <tr>
                                <th>Company</th>
                                <th>Stage</th>
                                <th>Categories</th>
                                <th>Submitted</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div class="dashboard-page__table__title">
                                        <div class="data-labels-set data-labels-set--xs">
                                            <span class="data-label data-label--lightning"><img src="/img-graph2.png" alt=""/></span>
                                        </div>
                                        <div>
                                            <h6 class="heading6"><a href="#">Sigma Logistics</a></h6>
                                            <p>Infrastructure for multi-cloud deployments</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    Series B
                                </td>
                                <td>
                                    <div class="status-plate-wrapper">
                                        <span class="status-plate status-plate--unreviewed">Cloud Infrastructure</span>
                                        <span class="status-plate status-plate--unreviewed">DevOps</span>
                                        <span class="status-plate status-plate--unreviewed">SaaS</span>
                                    </div>
                                </td>
                                <td>
                                    <a href="#" class="link link--view">4</a>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <span class="status-plate status-plate--strong status-plate--unreviewed">In Review</span>

                                        <Dropdown right class="dropdown-original icons-list dropdown-small ml-auto text-right">
                                        <DropdownItem class="icons-list__edit">
                                        Edit Group
                                        </DropdownItem>

                                        <DropdownItem
                                        class="icons-list__delete"
                                        >
                                        Remove Group
                                        </DropdownItem>
                                        </Dropdown>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div class="dashboard-page__table__title">
                                        <div class="data-labels-set data-labels-set--xs">
                                            <span class="data-label data-label--lightning"><img src="/img-graph2.png" alt=""/></span>
                                        </div>
                                        <div>
                                            <h6 class="heading6"><a href="#">Sigma Logistics</a></h6>
                                            <p>Infrastructure for multi-cloud deployments</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    Seed
                                </td>
                                <td>
                                    <div class="status-plate-wrapper">
                                        <span class="status-plate status-plate--unreviewed">Biotech</span>
                                        <span class="status-plate status-plate--unreviewed">Sustainability</span>
                                    </div>
                                </td>
                                <td>
                                    <a href="#" class="link link--view">9</a>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <span class="status-plate status-plate--strong status-plate--unreviewed">In Review +3 other</span>

                                        <Dropdown right class="dropdown-original icons-list dropdown-small ml-auto text-right">
                                        <DropdownItem class="icons-list__edit">
                                        Edit Group
                                        </DropdownItem>

                                        <DropdownItem
                                        class="icons-list__delete"
                                        >
                                        Remove Group
                                        </DropdownItem>
                                        </Dropdown>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div class="dashboard-page__table__title">
                                        <div class="data-labels-set data-labels-set--xs">
                                            <span class="data-label data-label--lightning"><img src="/img-graph2.png" alt=""/></span>
                                        </div>
                                        <div>
                                            <h6 class="heading6"><a href="#">Sigma Logistics</a></h6>
                                            <p>Infrastructure for multi-cloud deployments</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    Seed
                                </td>
                                <td>
                                    <div class="status-plate-wrapper">
                                        <span class="status-plate status-plate--unreviewed">Biotech</span>
                                        <span class="status-plate status-plate--unreviewed">Sustainability</span>
                                    </div>
                                </td>
                                <td>
                                    <a href="#" class="link link--view">9</a>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <span class="status-plate status-plate--rejected status-plate--strong">Rejected</span>

                                        <Dropdown right class="dropdown-original icons-list dropdown-small ml-auto text-right">
                                        <DropdownItem class="icons-list__edit">
                                        Edit Group
                                        </DropdownItem>

                                        <DropdownItem
                                        class="icons-list__delete"
                                        >
                                        Remove Group
                                        </DropdownItem>
                                        </Dropdown>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div class="dashboard-page__table__title">
                                        <div class="data-labels-set data-labels-set--xs">
                                            <span class="data-label data-label--lightning"><img src="/img-graph2.png" alt=""/></span>
                                        </div>
                                        <div>
                                            <h6 class="heading6"><a href="#">Sigma Logistics</a></h6>
                                            <p>Infrastructure for multi-cloud deployments</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    Seed
                                </td>
                                <td>
                                    <div class="status-plate-wrapper">
                                        <span class="status-plate status-plate--unreviewed">Biotech</span>
                                        <span class="status-plate status-plate--unreviewed">Sustainability</span>
                                    </div>
                                </td>
                                <td>
                                    <a href="#" class="link link--view">9</a>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <span class="status-plate status-plate--strong status-plate--evaluate">Escalated</span>

                                        <Dropdown right class="dropdown-original icons-list dropdown-small ml-auto text-right">
                                        <DropdownItem class="icons-list__edit">
                                        Edit Group
                                        </DropdownItem>

                                        <DropdownItem
                                        class="icons-list__delete"
                                        >
                                        Remove Group
                                        </DropdownItem>
                                        </Dropdown>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div class="dashboard-page__table__title">
                                        <div class="data-labels-set data-labels-set--xs">
                                            <span class="data-label data-label--lightning"><img src="/img-graph2.png" alt=""/></span>
                                        </div>
                                        <div>
                                            <h6 class="heading6"><a href="#">Sigma Logistics</a></h6>
                                            <p>Infrastructure for multi-cloud deployments</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    Seed
                                </td>
                                <td>
                                    <div class="status-plate-wrapper">
                                        <span class="status-plate status-plate--unreviewed">Biotech</span>
                                        <span class="status-plate status-plate--unreviewed">Sustainability</span>
                                    </div>
                                </td>
                                <td>
                                    <a href="#" class="link link--view">9</a>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center">
                                        <span class="status-plate status-plate--strong">Accepted</span>

                                        <Dropdown right class="dropdown-original icons-list dropdown-small ml-auto text-right">
                                        <DropdownItem class="icons-list__edit">
                                        Edit Group
                                        </DropdownItem>

                                        <DropdownItem
                                        class="icons-list__delete"
                                        >
                                        Remove Group
                                        </DropdownItem>
                                        </Dropdown>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

<!-- When empty -->
            <div id="" class="dashboard-page__card dashboard-page__card--round">
                <div class="dashboard-page__card__inner">
                    <header class="dashboard-page__card__header">
                        <h3 class="dashboard-page__title">My Startups</h3>
                    </header>

                    <div class="dashboard-page__empty">
                        <p>Start recommending your startups to enterprises</p>

                        <button class="button button-primary button--lg">Add Startups</button>
                    </div>
                </div>
            </div>

            <div class="showing-select">
                Showing {{gridParams.totalRowsCount}} startups
            </div>

            <ul class="list-cards">
                <li v-for="startup in items" :key="startup.id">
                    <div class="image-block">
                        <img :src="getLogo(startup.name.logoUrl)" :alt="startup.name.name">
                    </div>
                    <div class="list-cards__content">
                        <router-link class="heading5 heading5--tertiary" :to="`/startups/${startup.name.id}`">
                            {{ startup.name.name }}
                        </router-link>

                        <p>{{ startup.name.shortDescription }}</p>

                        <ul class="list-stat">
                            <li class="list-stat__funding" v-if="startup.total_raised">{{ startup.total_raised }}</li>
                            <li class="list-stat__location" v-if="startup.location.country">{{ startup.location.country }}<span v-if="startup.location.region">, {{ startup.location.region }}</span></li>
                            <li class="list-stat__location" v-if="!startup.location.country && startup.location.region">{{ startup.location.region }}</li>
                            <li class="list-stat__startups" v-if="startup.stage">{{ startup.stage }}</li>
                        </ul>
                    </div>

                    <a
                        v-if="startup.status === 'invite'"
                        class="invite-info"
                        @click="invite.open(startup.id)"
                    >
                        <span class="invite-info__invited">{{ startup.status }}</span>
                    </a>

                    <p class="invite-info" v-if="startup.status !== 'invite'">
                        {{ startup.status }}
                    </p>

                    <b-dropdown right text="Actions" class="list-cards__actions icons-list dropdown-simple dropdown-small" :disabled="removing">
                        <b-dropdown-item @click="remove(startup.id)" class="icons-list__delete">
                            Remove startup
                        </b-dropdown-item>

                        <b-dropdown-item
                            @click="addToPortfolio(startup.id)"
                            class="icons-list__add"
                        >
                            Add to a portfolio
                        </b-dropdown-item>
                    </b-dropdown>
                </li>
            </ul>

            <TablePager
                v-if="items.length"
                :pages-count="gridParams.pagesCount"
                :init-page="gridParams.currentPage"
                @changePage="gridMethods.changePage"
            />
        </main>

        <AddToPortfolios
            ref="addStartupsToPortfolios"
            :startups="selectedStartups"
        />

        <Invite
            ref="invite"
            @invited="gridMethods.resetList"
        />
    </div>
</template>

<style scoped>

</style>
